import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  CheckCircle,
  Copy,
  ExternalLink,
  Link2,
  Plus,
  RefreshCw,
  Users,
  XCircle
} from 'lucide-react';

import {
  useSettings
} from '../contexts/SettingsContext';

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

export default function PartnerClients() {
  const {
    darkMode
  } = useSettings();

  const [parceiros, setParceiros] =
    useState([]);

  const [clientes, setClientes] =
    useState([]);

  const [carregando, setCarregando] =
    useState(true);

  const [salvando, setSalvando] =
    useState(null);

  const [erro, setErro] =
    useState('');

  const [formulario, setFormulario] =
    useState({
      nome: '',
      telefone: '',
      email: ''
    });

  const [pagamentos, setPagamentos] =
    useState({});

  const linkDireto =
    window.location.origin +
    '/contratar-limpa-nome';

  const carregar =
    useCallback(async () => {
      setCarregando(true);
      setErro('');

      try {
        const [
          respostaParceiros,
          respostaClientes
        ] = await Promise.all([
          fetch(
            API + '/partner-clients',
            {
              credentials: 'include'
            }
          ),
          fetch(
            API + '/partner-clients/clients',
            {
              credentials: 'include'
            }
          )
        ]);

        const dadosParceiros =
          await respostaParceiros.json();

        const dadosClientes =
          await respostaClientes.json();

        if (!respostaParceiros.ok) {
          throw new Error(
            dadosParceiros.error ||
            'Não foi possível carregar os parceiros.'
          );
        }

        if (!respostaClientes.ok) {
          throw new Error(
            dadosClientes.error ||
            'Não foi possível carregar os clientes dos parceiros.'
          );
        }

        setParceiros(
          dadosParceiros.data || []
        );

        setClientes(
          dadosClientes.data || []
        );
      } catch (error) {
        setErro(error.message);
      } finally {
        setCarregando(false);
      }
    }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const copiar =
    async texto => {
      await navigator.clipboard.writeText(
        texto
      );

      window.alert(
        'Link copiado.'
      );
    };

  const criarParceiro =
    async evento => {
      evento.preventDefault();
      setSalvando('novo');

      try {
        const resposta =
          await fetch(
            API + '/partner-clients',
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type':
                  'application/json'
              },
              body:
                JSON.stringify(
                  formulario
                )
            }
          );

        const json =
          await resposta.json();

        if (!resposta.ok) {
          throw new Error(
            json.error ||
            'Não foi possível criar o parceiro.'
          );
        }

        setFormulario({
          nome: '',
          telefone: '',
          email: ''
        });

        await carregar();
      } catch (error) {
        window.alert(error.message);
      } finally {
        setSalvando(null);
      }
    };

  const alternarParceiro =
    async parceiro => {
      setSalvando(
        'parceiro-' + parceiro.id
      );

      try {
        const resposta =
          await fetch(
            API +
            '/partner-clients/' +
            parceiro.id,
            {
              method: 'PATCH',
              credentials: 'include',
              headers: {
                'Content-Type':
                  'application/json'
              },
              body:
                JSON.stringify({
                  ativo:
                    !parceiro.ativo
                })
            }
          );

        const json =
          await resposta.json();

        if (!resposta.ok) {
          throw new Error(
            json.error ||
            'Não foi possível alterar o parceiro.'
          );
        }

        await carregar();
      } catch (error) {
        window.alert(error.message);
      } finally {
        setSalvando(null);
      }
    };

  const renovarLink =
    async parceiro => {
      if (
        !window.confirm(
          'O link atual deixará de funcionar. Deseja gerar outro?'
        )
      ) {
        return;
      }

      setSalvando(
        'link-' + parceiro.id
      );

      try {
        const resposta =
          await fetch(
            API +
            '/partner-clients/' +
            parceiro.id +
            '/new-link',
            {
              method: 'POST',
              credentials: 'include'
            }
          );

        const json =
          await resposta.json();

        if (!resposta.ok) {
          throw new Error(
            json.error ||
            'Não foi possível renovar o link.'
          );
        }

        await carregar();
      } catch (error) {
        window.alert(error.message);
      } finally {
        setSalvando(null);
      }
    };

  const formatarValorPago =
      valor => {
        const original =
          String(valor ?? '').trim();

        if (!original) {
          return '';
        }

        let bruto =
          original.replace(
            /[^\d.,]/g,
            ''
          );

        if (
          !bruto.includes(',') &&
          /^\d+\.\d{1,2}$/.test(bruto) &&
          !/^\d{1,3}(?:\.\d{3})+$/.test(bruto)
        ) {
          bruto =
            bruto.replace('.', ',');
        } else {
          bruto =
            bruto.replace(/\./g, '');
        }

        const temVirgula =
          bruto.includes(',');

        const partes =
          bruto.split(',');

        const inteiroLimpo =
          String(partes[0] || '')
            .replace(/\D/g, '');

        if (
          !inteiroLimpo &&
          !temVirgula
        ) {
          return '';
        }

        const inteiro =
          Number(
            inteiroLimpo || '0'
          ).toLocaleString(
            'pt-BR',
            {
              maximumFractionDigits: 0
            }
          );

        const decimal =
          String(partes[1] || '')
            .replace(/\D/g, '')
            .slice(0, 2);

        return temVirgula
          ? inteiro + ',' + decimal
          : inteiro;
      };

    const finalizarValorPago =
      valor => {
        const formatado =
          formatarValorPago(valor);

        if (!formatado) {
          return '';
        }

        const partes =
          formatado.split(',');

        const decimal =
          (
            String(partes[1] || '') +
            '00'
          ).slice(0, 2);

        return (
          partes[0] +
          ',' +
          decimal
        );
      };

    const alterarPagamento =
    (
      leadId,
      campo,
      valor
    ) => {
      setPagamentos({
        ...pagamentos,
        [leadId]: {
          valor_pago:
            pagamentos[leadId]
              ?.valor_pago || '',
          garantia_meses:
            pagamentos[leadId]
              ?.garantia_meses ?? '',
          [campo]:
            valor
        }
      });
    };

  const confirmarPagamento =
    async cliente => {
      const dados =
        pagamentos[cliente.id] ||
        {};

      if (!dados.valor_pago) {
        window.alert(
          'Informe o valor pago pelo parceiro.'
        );

        return;
      }

      if (
        dados.garantia_meses === '' ||
        dados.garantia_meses ===
          undefined
      ) {
        window.alert(
          'Escolha o período de garantia.'
        );

        return;
      }

      if (
        !window.confirm(
          'Confirma o pagamento e a inclusão deste cliente no próximo lote?'
        )
      ) {
        return;
      }

      setSalvando(
        'pagamento-' + cliente.id
      );

      try {
        const resposta =
          await fetch(
            API +
            '/partner-clients/' +
            cliente.partner_id +
            '/clients/' +
            cliente.id +
            '/confirm-payment',
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type':
                  'application/json'
              },
              body:
                JSON.stringify({
                  valor_pago:
                    dados.valor_pago,
                  garantia_meses:
                    Number(
                      dados.garantia_meses
                    )
                })
            }
          );

        const json =
          await resposta.json();

        if (!resposta.ok) {
          throw new Error(
            json.error ||
            'Não foi possível confirmar o pagamento.'
          );
        }

        window.alert(
          'Pagamento confirmado. O cliente foi incluído no próximo lote.'
        );

        await carregar();
      } catch (error) {
        window.alert(error.message);
      } finally {
        setSalvando(null);
      }
    };

  const linkDoParceiro =
    parceiro =>
      window.location.origin +
      '/parceiro/' +
      parceiro.public_token;

  const clientesDoParceiro =
    parceiroId =>
      clientes.filter(
        cliente =>
          Number(cliente.partner_id) ===
          Number(parceiroId)
      );

  const clientesDiretos =
    clientes.filter(
      cliente =>
        cliente.source === 'direct_link' &&
        !cliente.partner_id
    );

  const dataBrasileira =
    valor =>
      valor
        ? new Date(valor)
            .toLocaleDateString(
              'pt-BR'
            )
        : '-';

  const card =
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white'
      : 'bg-white border-gray-200 text-gray-900';

  return (
    <div className="space-y-6">
      <div>
        <h1 className={
          'text-3xl font-bold ' +
          (
            darkMode
              ? 'text-white'
              : 'text-gray-900'
          )
        }>
          Clientes Parceiros
        </h1>

        <p className={
          darkMode
            ? 'text-gray-400'
            : 'text-gray-600'
        }>
          Links de atendimento e controle dos pagamentos feitos pelos parceiros.
        </p>
      </div>

      {erro && (
        <div className="rounded-xl border border-red-400 bg-red-50 p-4 text-red-700">
          {erro}
        </div>
      )}

      <div className={
        'rounded-xl border p-5 ' +
        card
      }>
        <div className="flex items-center gap-2">
          <Link2
            size={20}
            className="text-blue-500"
          />

          <h2 className="text-lg font-bold">
            Link oficial Financy
          </h2>

          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-bold text-green-700">
            PERMANENTE
          </span>
        </div>

        <p className={
          'mt-2 text-sm ' +
          (
            darkMode
              ? 'text-gray-400'
              : 'text-gray-600'
          )
        }>
          Clientes diretos escolhem o serviço, a forma de pagamento e seguem para o contrato.
        </p>

        <div className="mt-4 flex flex-col gap-3 lg:flex-row">
          <input
            readOnly
            value={linkDireto}
            className={
              'min-w-0 flex-1 rounded-lg border px-3 py-3 ' +
              (
                darkMode
                  ? 'border-gray-600 bg-gray-900 text-white'
                  : 'border-gray-300 bg-gray-50'
              )
            }
          />

          <button
            onClick={() =>
              copiar(linkDireto)
            }
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700"
          >
            <Copy size={17} />
            Copiar link
          </button>

          <a
            href={linkDireto}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 rounded-lg border border-gray-500 px-4 py-3"
          >
            <ExternalLink size={17} />
            Abrir
          </a>
        </div>

        <div className="mt-5 border-t border-gray-700 pt-4">
          <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
            <Users size={15} />
            {clientesDiretos.length} cliente(s) pelo link oficial
          </div>

          {clientesDiretos.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nenhum cliente cadastrado pelo link oficial.
            </p>
          ) : (
            <div className="space-y-3">
              {clientesDiretos.map(cliente => (
                <div
                  key={cliente.id}
                  className={
                    'rounded-lg border p-4 ' +
                    (darkMode
                      ? 'border-gray-700 bg-gray-900'
                      : 'border-gray-200 bg-gray-50')
                  }
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold">{cliente.nome}</h3>
                      <p className="text-sm text-gray-500">{cliente.cpf_cnpj}</p>
                      <p className="text-sm text-gray-500">
                        Cadastro em {dataBrasileira(cliente.created_at)}
                      </p>
                    </div>

                    <span className={
                      'rounded-full px-3 py-1 text-xs font-bold ' +
                      (cliente.pagamento_confirmado
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700')
                    }>
                      {cliente.pagamento_confirmado
                        ? 'PAGAMENTO CONFIRMADO'
                        : 'AGUARDANDO PAGAMENTO'}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      {cliente.contrato_status === 'signed' ? (
                        <CheckCircle size={17} className="text-green-500" />
                      ) : (
                        <XCircle size={17} className="text-red-500" />
                      )}
                      Contrato assinado
                    </span>

                    <span className="flex items-center gap-1">
                      {cliente.incluido_processos ? (
                        <CheckCircle size={17} className="text-green-500" />
                      ) : (
                        <XCircle size={17} className="text-yellow-500" />
                      )}
                      {cliente.incluido_processos
                        ? 'Incluído em Processos'
                        : 'Aguardando envio para Processos'}
                    </span>

                    {String(cliente.conversation_stage || '').startsWith('coletando_') && (
                      <span className="flex items-center gap-1">
                        <XCircle size={17} className="text-yellow-500" />
                        Documentos pendentes
                      </span>
                    )}
                  </div>

                  <div className="mt-3 text-sm text-gray-500">
                    Entrada: {Number(cliente.valor_entrada || 0).toLocaleString(
                      'pt-BR',
                      { style: 'currency', currency: 'BRL' }
                    )}
                    {' · '}
                    Etapa: {cliente.conversation_stage || '-'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <form
        onSubmit={criarParceiro}
        className={
          'rounded-xl border p-5 ' +
          card
        }
      >
        <h2 className="text-lg font-bold">
          Criar link de parceiro
        </h2>

        <p className={
          'mt-1 text-sm ' +
          (
            darkMode
              ? 'text-gray-400'
              : 'text-gray-600'
          )
        }>
          O link do parceiro não mostra preços e não gera contrato.
        </p>

        <div className="mt-4 grid gap-3 lg:grid-cols-4">
          <input
            required
            value={formulario.nome}
            onChange={evento =>
              setFormulario({
                ...formulario,
                nome:
                  evento.target.value
              })
            }
            placeholder="Nome do parceiro ou escritório"
            className={
              'rounded-lg border px-3 py-3 ' +
              (
                darkMode
                  ? 'border-gray-600 bg-gray-900 text-white'
                  : 'border-gray-300'
              )
            }
          />

          <input
            value={formulario.telefone}
            onChange={evento =>
              setFormulario({
                ...formulario,
                telefone:
                  evento.target.value
              })
            }
            placeholder="Telefone opcional"
            className={
              'rounded-lg border px-3 py-3 ' +
              (
                darkMode
                  ? 'border-gray-600 bg-gray-900 text-white'
                  : 'border-gray-300'
              )
            }
          />

          <input
            type="email"
            value={formulario.email}
            onChange={evento =>
              setFormulario({
                ...formulario,
                email:
                  evento.target.value
              })
            }
            placeholder="E-mail opcional"
            className={
              'rounded-lg border px-3 py-3 ' +
              (
                darkMode
                  ? 'border-gray-600 bg-gray-900 text-white'
                  : 'border-gray-300'
              )
            }
          />

          <button
            disabled={
              salvando === 'novo'
            }
            className="flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-3 font-semibold text-white disabled:opacity-50"
          >
            <Plus size={18} />
            Criar parceiro
          </button>
        </div>
      </form>

      {carregando ? (
        <div className="flex justify-center py-10">
          <RefreshCw className="animate-spin" />
        </div>
      ) : (
        <div className="grid gap-5">
          {parceiros.map(parceiro => {
            const link =
              linkDoParceiro(
                parceiro
              );

            const encaminhados =
              clientesDoParceiro(
                parceiro.id
              );

            return (
              <div
                key={parceiro.id}
                className={
                  'rounded-xl border p-5 ' +
                  card
                }
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-bold">
                        {parceiro.nome}
                      </h2>

                      <span className={
                        'rounded-full px-2 py-1 text-xs font-bold ' +
                        (
                          parceiro.ativo
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        )
                      }>
                        {parceiro.ativo
                          ? 'LINK ATIVO'
                          : 'DESATIVADO'}
                      </span>
                    </div>

                    <p className="mt-1 flex items-center gap-1 text-sm text-gray-500">
                      <Users size={15} />
                      {encaminhados.length} cliente(s)
                    </p>
                  </div>

                  <button
                    disabled={
                      salvando ===
                      'parceiro-' +
                      parceiro.id
                    }
                    onClick={() =>
                      alternarParceiro(
                        parceiro
                      )
                    }
                    className={
                      'rounded-lg border px-3 py-2 text-sm font-semibold ' +
                      (
                        parceiro.ativo
                          ? 'border-red-400 text-red-500'
                          : 'border-green-400 text-green-500'
                      )
                    }
                  >
                    {parceiro.ativo
                      ? 'Desativar'
                      : 'Ativar'}
                  </button>
                </div>

                <div className="mt-4 flex flex-col gap-2 lg:flex-row">
                  <input
                    readOnly
                    value={link}
                    className={
                      'min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm ' +
                      (
                        darkMode
                          ? 'border-gray-600 bg-gray-900'
                          : 'border-gray-300 bg-gray-50'
                      )
                    }
                  />

                  <button
                    onClick={() =>
                      copiar(link)
                    }
                    className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white"
                  >
                    <Copy size={16} />
                    Copiar
                  </button>

                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 rounded-lg border border-gray-500 px-3 py-2 text-sm"
                  >
                    <ExternalLink size={16} />
                    Abrir
                  </a>

                  <button
                    onClick={() =>
                      renovarLink(
                        parceiro
                      )
                    }
                    className="flex items-center justify-center gap-2 rounded-lg border border-orange-400 px-3 py-2 text-sm text-orange-500"
                  >
                    <RefreshCw size={16} />
                    Novo link
                  </button>
                </div>

                <div className="mt-5 space-y-3">
                  {encaminhados.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Nenhum cliente encaminhado por este parceiro.
                    </p>
                  ) : (
                    encaminhados.map(cliente => (
                      <div
                        key={cliente.id}
                        className={
                          'rounded-lg border p-4 ' +
                          (
                            darkMode
                              ? 'border-gray-700 bg-gray-900'
                              : 'border-gray-200 bg-gray-50'
                          )
                        }
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h3 className="font-bold">
                              {cliente.nome}
                            </h3>

                            <p className="text-sm text-gray-500">
                              {cliente.cpf_cnpj}
                            </p>

                            <p className="text-sm text-gray-500">
                              Cadastro em{' '}
                              {dataBrasileira(
                                cliente.created_at
                              )}
                            </p>
                          </div>

                          <span className={
                            'rounded-full px-3 py-1 text-xs font-bold ' +
                            (
                              cliente.pagamento_confirmado
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            )
                          }>
                            {cliente.pagamento_confirmado
                              ? 'PAGAMENTO CONFIRMADO'
                              : 'AGUARDANDO PAGAMENTO'}
                          </span>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            {cliente.possui_ficha ? (
                              <CheckCircle
                                size={17}
                                className="text-green-500"
                              />
                            ) : (
                              <XCircle
                                size={17}
                                className="text-red-500"
                              />
                            )}
                            Ficha assinada
                          </span>

                          <span className="flex items-center gap-1">
                            {cliente.possui_serasa ? (
                              <CheckCircle
                                size={17}
                                className="text-green-500"
                              />
                            ) : (
                              <XCircle
                                size={17}
                                className="text-red-500"
                              />
                            )}
                            Consulta Serasa
                          </span>

                          <span className="flex items-center gap-1">
                            {cliente.possui_protestos ? (
                              <CheckCircle
                                size={17}
                                className="text-green-500"
                              />
                            ) : (
                              <XCircle
                                size={17}
                                className="text-red-500"
                              />
                            )}
                            Consulta de Protestos
                          </span>
                        </div>

                        {!cliente.pagamento_confirmado && (
                          <div className="mt-4 grid gap-3 md:grid-cols-3">
                            <input
                              value={
                                pagamentos[
                                  cliente.id
                                ]?.valor_pago ||
                                ''
                              }
                              onChange={evento =>
                                alterarPagamento(
                                  cliente.id,
                                  'valor_pago',
                                  formatarValorPago(
                                    evento.target.value
                                  )
                                )
                              }
                              onBlur={evento =>
                                  alterarPagamento(
                                    cliente.id,
                                    'valor_pago',
                                    finalizarValorPago(
                                      evento.target.value
                                    )
                                  )
                                }
                                inputMode="decimal"
                                placeholder="Valor pago pelo parceiro"
                              className={
                                'rounded-lg border px-3 py-2 ' +
                                (
                                  darkMode
                                    ? 'border-gray-600 bg-gray-800 text-white'
                                    : 'border-gray-300 bg-white'
                                )
                              }
                            />

                            <select
                              value={
                                pagamentos[
                                  cliente.id
                                ]?.garantia_meses ??
                                ''
                              }
                              onChange={evento =>
                                alterarPagamento(
                                  cliente.id,
                                  'garantia_meses',
                                  evento.target.value
                                )
                              }
                              className={
                                'rounded-lg border px-3 py-2 ' +
                                (
                                  darkMode
                                    ? 'border-gray-600 bg-gray-800 text-white'
                                    : 'border-gray-300 bg-white'
                                )
                              }
                            >
                              <option value="">
                                Escolha a garantia
                              </option>

                              <option value="0">
                                Sem garantia
                              </option>

                              {Array.from(
                                {
                                  length: 12
                                },
                                (_, indice) =>
                                  indice + 1
                              ).map(meses => (
                                <option
                                  key={meses}
                                  value={meses}
                                >
                                  {meses}{' '}
                                  {meses === 1
                                    ? 'mês'
                                    : 'meses'}
                                </option>
                              ))}
                            </select>

                            <button
                              disabled={
                                salvando ===
                                'pagamento-' +
                                cliente.id
                              }
                              onClick={() =>
                                confirmarPagamento(
                                  cliente
                                )
                              }
                              className="rounded-lg bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              Confirmar pagamento
                            </button>
                          </div>
                        )}

                        {cliente.pagamento_confirmado && (
                          <div className="mt-3 text-sm text-gray-500">
                            Valor: {' '}
                            {Number(
                              cliente.valor_total || 0
                            ).toLocaleString(
                              'pt-BR',
                              {
                                style:
                                  'currency',
                                currency:
                                  'BRL'
                              }
                            )}
                            {' · '}
                            Garantia: {' '}
                            {Number(
                              cliente.garantia_meses || 0
                            ) === 0
                              ? 'sem garantia'
                              : cliente.garantia_meses +
                                ' mês(es)'}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
